import { useMutation, useQuery } from '@apollo/client/react';
import { type PropsWithChildren, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import FileInput from '@/components/FileInput';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldGroup,
  FieldLabel
} from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { fileToBase64 } from '@/lib/utils';

import { GET_GOVERNANCE_OVERVIEW, UPSERT_GOVERNANCE_OVERVIEW } from './graphql';
import type { GetGovernanceOverviewResponse } from './types';

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{ title: string }>) => (
  <div className="mb-2 flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

const GovernanceTab = ({ dealId }: { dealId: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data, loading } = useQuery<
    GetGovernanceOverviewResponse,
    { dealOpportunityId: string }
  >(GET_GOVERNANCE_OVERVIEW, {
    variables: { dealOpportunityId: dealId }
  });

  const [upsertGovernance, { loading: isUpdating }] = useMutation(
    UPSERT_GOVERNANCE_OVERVIEW,
    { refetchQueries: [GET_GOVERNANCE_OVERVIEW] }
  );

  if (loading) {
    return (
      <div className="grid h-64 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  const governance = data?.governanceOverviewByDeal;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);

      const boardText = formData.get('board-structure-field');
      const boardOfDirectorsStructure =
        typeof boardText === 'string' && boardText.trim()
          ? boardText.trim()
          : null;

      const ownershipFile = formData.get('ownership-image-field');
      let ownershipPctImage: null | string = null;
      if (ownershipFile instanceof File && ownershipFile.size > 0) {
        ownershipPctImage = await fileToBase64(ownershipFile);
      }

      const managementFile = formData.get('management-image-field');
      let managementImage: null | string = null;
      if (managementFile instanceof File && managementFile.size > 0) {
        managementImage = await fileToBase64(managementFile);
      }

      const values: Record<string, unknown> = {
        dealOpportunityId: dealId
      };
      if (boardOfDirectorsStructure !== null) {
        values.boardOfDirectorsStructure = boardOfDirectorsStructure;
      }
      if (ownershipPctImage !== null) {
        values.ownershipPctImage = ownershipPctImage;
      }
      if (managementImage !== null) {
        values.managementImage = managementImage;
      }

      const { errors } = await upsertGovernance({
        variables: { values }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      setIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-3 pt-5">
      <SectionHeader title="Post Close Governance & Ownership Alignment">
        <Dialog onOpenChange={setIsOpen} open={isOpen}>
          <DialogTrigger asChild>
            <EditButton />
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                Post Close Governance & Ownership Alignment
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id="governance-form"
                noValidate
                onSubmit={event => void handleSubmit(event)}
              >
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="board-structure-field">
                      Board of Directors Structure
                    </FieldLabel>
                    <Textarea
                      defaultValue={
                        governance?.boardOfDirectorsStructure ?? ''
                      }
                      id="board-structure-field"
                      name="board-structure-field"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="ownership-image-field">
                      Ownership PCT Image
                    </FieldLabel>
                    <FileInput
                      accept="image/*"
                      data={governance?.ownershipPctImage ?? null}
                      id="ownership-image-field"
                      name="ownership-image-field"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="management-image-field">
                      Management Image
                    </FieldLabel>
                    <FileInput
                      accept="image/*"
                      data={governance?.managementImage ?? null}
                      id="management-image-field"
                      name="management-image-field"
                    />
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isUpdating}
                form="governance-form"
                type="submit"
              >
                {isUpdating && <Spinner />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SectionHeader>
      <Card>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">
              Board of Directors Structure
            </div>
            <p className="text-muted-foreground text-sm whitespace-pre-wrap">
              {governance?.boardOfDirectorsStructure || '-'}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <div className="text-sm font-medium">Ownership PCT Image</div>
              <FileInput
                accept="image/*"
                data={governance?.ownershipPctImage ?? null}
                readOnly
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Management Image</div>
              <FileInput
                accept="image/*"
                data={governance?.managementImage ?? null}
                readOnly
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GovernanceTab;
